import { useMutation } from "@apollo/client";
import Undo from "@mui/icons-material/Undo";
import { Button } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useState } from "react";

import { MANIFEST_LIST_QUERIES, UNCONFIRM_BILL_OF_LADING } from "./api";

type ReopenableBill = {
  documentManifestId?: string | null;
  sourceDocument?: { documentMetadata?: { needsReview?: boolean | null } | null } | null;
};

/**
 * Staff action that puts a confirmed bill of lading back to needing review.
 * The saved manifest and its lines stay as they are. Renders nothing for
 * non-staff, or while the bill still needs review.
 */
export function ReopenBillOfLadingForReviewAction({
  show,
  manifest,
  onReopened,
}: {
  show: boolean;
  manifest: ReopenableBill;
  onReopened: () => void;
}) {
  const { isStaff } = useCurrentUser();
  const alerts = useAlerts();
  const [open, setOpen] = useState(false);
  const [reopen, { loading }] = useMutation(UNCONFIRM_BILL_OF_LADING, {
    refetchQueries: MANIFEST_LIST_QUERIES,
  });

  const wasteManifestId = manifest.documentManifestId;
  const needsReview = !!manifest.sourceDocument?.documentMetadata?.needsReview;
  if (!show || !isStaff || needsReview || !wasteManifestId) return null;

  const confirm = async () => {
    try {
      await reopen({ variables: { wasteManifestId } });
      alerts.success("Bill of lading reopened for review.");
      setOpen(false);
      onReopened();
    } catch (error) {
      alerts.error(
        "Could not reopen the bill of lading",
        error instanceof Error ? error.message : undefined
      );
    }
  };

  return (
    <>
      <Button size="small" color="staff" startIcon={<Undo />} onClick={() => setOpen(true)}>
        Reopen for review
      </Button>
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={confirm}
        header="Reopen this bill of lading for review?"
        info="The saved lines stay as they are. The bill shows as needing review until someone confirms it again."
        confirmText="Reopen"
        loading={loading}
      />
    </>
  );
}
